import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Plus, Building2, MapPin, Edit, Trash2, Loader2, X, Users, Globe, Search, DollarSign, Briefcase, RotateCcw, RefreshCw } from 'lucide-react';
import AdvancedDataGrid from '../../components/AdvancedDataGrid';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const COUNTRIES = ['USA', 'Canada', 'UK', 'India', 'Australia', 'Germany', 'France', 'Other'];
const ATS_OPTIONS = ['JobDiva', 'BullHorn', 'Salesforce', 'Greenhouse', 'Workday', 'iCIMS', 'Taleo'];
const AI_VOICE_OPTIONS = ['JobTalk', 'ConverzAI', 'HeyMilo', 'Glider', 'Paradox', 'HireVue'];

export default function CompaniesPage() {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [formData, setFormData] = useState({ name: '', locations: [], ats_used: '', ai_voice_using: '', other_ai_tools: '' });
  const [saving, setSaving] = useState(false);
  
  // Selection for bulk actions
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  
  // Cache reference
  const cacheRef = useRef({
    companies: null,
    timestamp: null
  });
  const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  useEffect(() => {
    fetchCompanies(false);
  }, []);

  const fetchCompanies = async (forceRefresh = false) => {
    const now = Date.now();
    const cache = cacheRef.current;
    
    // Use cached data if valid and not forcing refresh
    if (!forceRefresh && cache.timestamp && (now - cache.timestamp) < CACHE_DURATION && cache.companies) {
      setCompanies(cache.companies);
      setLoading(false);
      return;
    }
    
    if (forceRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    
    try {
      const response = await axios.get(`${API}/companies?limit=1000`);
      const data = response.data.items || [];
      
      // Update cache
      cacheRef.current = {
        companies: data,
        timestamp: Date.now()
      };
      
      setCompanies(data);
      
      if (forceRefresh) {
        toast.success('Companies data refreshed');
      }
    } catch (error) {
      toast.error('Failed to load companies');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };
  
  // Reset filters and show cached data
  const handleReset = () => {
    setSearchQuery('');
    setSelectedIds(new Set());
    toast.success('Filters cleared');
  };
  
  // Force refresh from database
  const handleRefresh = () => {
    fetchCompanies(true);
  };

  // Column definitions for AdvancedDataGrid
  const columns = [
    {
      key: 'name',
      label: 'Company Name',
      sortable: true,
      filterable: true,
      required: true,
      render: (row) => (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
            <Building2 className="h-5 w-5 text-indigo-600" />
          </div>
          <div>
            <div className="font-medium text-slate-900">{row.name}</div>
            <div className="text-xs text-slate-500">{row.locations?.length || 0} location(s)</div>
          </div>
        </div>
      )
    },
    {
      key: 'locations_count',
      label: 'Locations',
      sortable: true,
      render: (row) => (
        <Badge variant="outline" className="bg-slate-50">
          <MapPin className="h-3 w-3 mr-1" />
          {row.locations?.length || 0}
        </Badge>
      )
    },
    {
      key: 'primary_country',
      label: 'Country',
      sortable: true,
      filterable: true,
      filterType: 'status',
      filterOptions: COUNTRIES.map(c => ({ value: c, label: c })),
      render: (row) => {
        const primaryLocation = row.locations?.[0];
        return primaryLocation?.country ? (
          <div className="flex items-center gap-2">
            <Globe className="h-4 w-4 text-slate-400" />
            <span>{primaryLocation.country}</span>
          </div>
        ) : <span className="text-slate-400">—</span>;
      }
    },
    {
      key: 'primary_city',
      label: 'City',
      sortable: true,
      filterable: true,
      render: (row) => {
        const primaryLocation = row.locations?.[0];
        return primaryLocation?.city || <span className="text-slate-400">—</span>;
      }
    },
    {
      key: 'leads_count',
      label: 'Leads',
      sortable: true,
      sortType: 'number',
      render: (row) => (
        <Badge className="bg-blue-100 text-blue-700">
          <Users className="h-3 w-3 mr-1" />
          {row.leads_count || 0}
        </Badge>
      )
    },
    {
      key: 'created_at',
      label: 'Created',
      sortable: true,
      sortType: 'date',
      render: (row) => row.created_at ? format(parseISO(row.created_at), 'MMM d, yyyy') : '—'
    }
  ];

  const handleCreate = async () => {
    if (!formData.name.trim()) {
      toast.error('Company name is required');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/companies`, formData);
      toast.success('Company created');
      setCreateDialogOpen(false);
      setFormData({ name: '', locations: [], ats_used: '', ai_voice_using: '', other_ai_tools: '' });
      fetchCompanies(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create company');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!formData.name.trim()) {
      toast.error('Company name is required');
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/companies/${selectedCompany.id}`, formData);
      toast.success('Company updated');
      setEditDialogOpen(false);
      fetchCompanies(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update company');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/companies/${selectedCompany.id}`);
      toast.success('Company deleted');
      setDeleteDialogOpen(false);
      fetchCompanies(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete company');
    }
  };

  const handleBulkDelete = async () => {
    try {
      await Promise.all(Array.from(selectedIds).map(id => axios.delete(`${API}/companies/${id}`)));
      toast.success(`${selectedIds.size} companies deleted`);
      setBulkDeleteDialogOpen(false);
      setSelectedIds(new Set());
      fetchCompanies(true);
    } catch (error) {
      toast.error('Failed to delete some companies');
    }
  };

  const openEditDialog = (company) => {
    setSelectedCompany(company);
    setFormData({ 
      name: company.name, 
      locations: company.locations || [],
      ats_used: company.ats_used || '',
      ai_voice_using: company.ai_voice_using || '',
      other_ai_tools: company.other_ai_tools || ''
    });
    setEditDialogOpen(true);
  };

  const addLocation = () => {
    setFormData({
      ...formData,
      locations: [...formData.locations, { 
        id: crypto.randomUUID(),
        name: '', address: '', city: '', state: '', country: 'USA', phone: ''
      }]
    });
  };

  const updateLocation = (index, field, value) => {
    const newLocations = [...formData.locations];
    newLocations[index] = { ...newLocations[index], [field]: value };
    setFormData({ ...formData, locations: newLocations });
  };

  const removeLocation = (index) => {
    setFormData({ ...formData, locations: formData.locations.filter((_, i) => i !== index) });
  };

  // Render row actions
  const renderRowActions = (row) => (
    <div className="flex items-center gap-1">
      <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); openEditDialog(row); }} data-testid={`edit-company-${row.id}`}>
        <Edit className="h-4 w-4" />
      </Button>
      <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50" 
        onClick={(e) => { e.stopPropagation(); setSelectedCompany(row); setDeleteDialogOpen(true); }} 
        data-testid={`delete-company-${row.id}`}>
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  );

  // Stats
  const stats = {
    total: companies.length,
    totalLocations: companies.reduce((acc, c) => acc + (c.locations?.length || 0), 0),
    countries: [...new Set(companies.flatMap(c => c.locations?.map(l => l.country) || []))].length,
    totalRevenue: companies.reduce((acc, c) => acc + (parseFloat(c.total_revenue) || 0), 0),
    totalJobs: companies.reduce((acc, c) => acc + (parseInt(c.total_jobs) || 0), 0),
  };

  // Filter companies based on search query
  const filteredCompanies = useMemo(() => {
    if (!searchQuery.trim()) return companies;
    const query = searchQuery.toLowerCase();
    return companies.filter(company => 
      (company.name || '').toLowerCase().includes(query) ||
      company.locations?.some(loc => 
        (loc.city || '').toLowerCase().includes(query) ||
        (loc.country || '').toLowerCase().includes(query) ||
        (loc.name || '').toLowerCase().includes(query)
      )
    );
  }, [companies, searchQuery]);

  // Location Form Component
  const LocationForm = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label className="text-base font-semibold">Locations</Label>
        <Button type="button" variant="outline" size="sm" onClick={addLocation} data-testid="add-location-btn">
          <Plus className="h-4 w-4 mr-2" /> Add Location
        </Button>
      </div>
      {formData.locations.length === 0 ? (
        <div className="text-center py-6 border border-dashed rounded-lg">
          <MapPin className="h-8 w-8 text-slate-300 mx-auto mb-2" />
          <p className="text-sm text-slate-500">No locations yet. Add one above.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {formData.locations.map((loc, index) => (
            <div key={loc.id || index} className="border rounded-lg p-4 space-y-3 bg-slate-50">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-slate-700">Location {index + 1}</span>
                <Button type="button" variant="ghost" size="sm" onClick={() => removeLocation(index)} className="text-red-600 hover:text-red-700">
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Location Name</Label>
                  <Input value={loc.name} onChange={(e) => updateLocation(index, 'name', e.target.value)} placeholder="e.g., Headquarters" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Phone</Label>
                  <Input value={loc.phone} onChange={(e) => updateLocation(index, 'phone', e.target.value)} placeholder="+1 (555) 123-4567" />
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Address</Label>
                <Input value={loc.address} onChange={(e) => updateLocation(index, 'address', e.target.value)} placeholder="123 Main Street" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">City</Label>
                  <Input value={loc.city} onChange={(e) => updateLocation(index, 'city', e.target.value)} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">State</Label>
                  <Input value={loc.state} onChange={(e) => updateLocation(index, 'state', e.target.value)} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Country</Label>
                  <Select value={loc.country} onValueChange={(v) => updateLocation(index, 'country', v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {COUNTRIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-6 animate-fade-in w-full">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            CRM Companies
          </h1>
          <p className="text-slate-600 mt-1">Manage companies and their locations</p>
        </div>
        <Button onClick={() => { setFormData({ name: '', locations: [], ats_used: '', ai_voice_using: '', other_ai_tools: '' }); setCreateDialogOpen(true); }} data-testid="add-company-btn">
          <Plus className="h-4 w-4 mr-2" /> Add Company
        </Button>
      </div>

      {/* Search Box with Reset and Refresh */}
      <div className="flex items-center gap-2 max-w-2xl">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
          <Input
            placeholder="Search by company name, city, or country..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 pr-10 h-11 bg-white border-slate-200 focus:border-indigo-500"
            data-testid="companies-search"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>
        <Button
          variant="outline"
          size="icon"
          onClick={handleReset}
          title="Reset filters (use cached data)"
          className="h-11 w-11 shrink-0"
          data-testid="reset-companies-btn"
        >
          <RotateCcw className="h-4 w-4" />
        </Button>
        <Button
          variant="outline"
          size="icon"
          onClick={handleRefresh}
          disabled={refreshing}
          title="Refresh data from database"
          className="h-11 w-11 shrink-0"
          data-testid="refresh-companies-btn"
        >
          {refreshing ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4" />
          )}
        </Button>
      </div>
      {searchQuery && (
        <p className="text-sm text-slate-500">
          Found {filteredCompanies.length} {filteredCompanies.length === 1 ? 'company' : 'companies'} matching &quot;{searchQuery}&quot;
        </p>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card className="shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-indigo-100 rounded-lg flex items-center justify-center">
                <Building2 className="h-4 w-4 text-indigo-600" />
              </div>
              <div>
                <p className="text-xl font-bold">{stats.total}</p>
                <p className="text-xs text-slate-500">Companies</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-green-100 rounded-lg flex items-center justify-center">
                <MapPin className="h-4 w-4 text-green-600" />
              </div>
              <div>
                <p className="text-xl font-bold">{stats.totalLocations}</p>
                <p className="text-xs text-slate-500">Locations</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-blue-100 rounded-lg flex items-center justify-center">
                <Globe className="h-4 w-4 text-blue-600" />
              </div>
              <div>
                <p className="text-xl font-bold">{stats.countries}</p>
                <p className="text-xs text-slate-500">Countries</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-emerald-100 rounded-lg flex items-center justify-center">
                <DollarSign className="h-4 w-4 text-emerald-600" />
              </div>
              <div>
                <p className="text-xl font-bold">{stats.totalRevenue >= 1000000 ? `${(stats.totalRevenue / 1000000).toFixed(1)}M` : stats.totalRevenue >= 1000 ? `${(stats.totalRevenue / 1000).toFixed(0)}K` : stats.totalRevenue.toFixed(0)}</p>
                <p className="text-xs text-slate-500">Revenue</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-purple-100 rounded-lg flex items-center justify-center">
                <Briefcase className="h-4 w-4 text-purple-600" />
              </div>
              <div>
                <p className="text-xl font-bold">{stats.totalJobs >= 1000 ? `${(stats.totalJobs / 1000).toFixed(1)}K` : stats.totalJobs}</p>
                <p className="text-xs text-slate-500">Jobs</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Advanced Data Grid */}
      <AdvancedDataGrid
        columns={columns}
        data={filteredCompanies}
        loading={loading}
        onRowClick={(row) => navigate(`/admin/companies/${row.id}`)}
        renderRowActions={renderRowActions}
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
        onBulkDelete={() => setBulkDeleteDialogOpen(true)}
        rowIdKey="id"
        emptyMessage={searchQuery ? "No companies match your search" : "No companies found. Add your first company!"}
        emptyIcon={<Building2 className="h-12 w-12 text-slate-300 mx-auto mb-4" />}
      />

      {/* Create Company Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Add New Company</DialogTitle></DialogHeader>
          <div className="space-y-6 py-4">
            <div className="space-y-2">
              <Label>Company Name *</Label>
              <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder="e.g., Acme Corporation" data-testid="company-name" />
            </div>
            
            {/* Tech Stack Section */}
            <div className="border-t pt-4">
              <h3 className="font-medium text-slate-900 mb-4">Tech Stack</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>ATS Used</Label>
                  <Input 
                    list="ats-options"
                    value={formData.ats_used} 
                    onChange={(e) => setFormData({...formData, ats_used: e.target.value})} 
                    placeholder="e.g., JobDiva"
                    data-testid="ats-used"
                  />
                  <datalist id="ats-options">
                    {ATS_OPTIONS.map(opt => <option key={opt} value={opt} />)}
                  </datalist>
                </div>
                <div className="space-y-2">
                  <Label>AI Voice Using</Label>
                  <Input 
                    list="ai-voice-options"
                    value={formData.ai_voice_using} 
                    onChange={(e) => setFormData({...formData, ai_voice_using: e.target.value})} 
                    placeholder="e.g., JobTalk"
                    data-testid="ai-voice-using"
                  />
                  <datalist id="ai-voice-options">
                    {AI_VOICE_OPTIONS.map(opt => <option key={opt} value={opt} />)}
                  </datalist>
                </div>
                <div className="space-y-2">
                  <Label>Other AI Tools</Label>
                  <Input 
                    value={formData.other_ai_tools} 
                    onChange={(e) => setFormData({...formData, other_ai_tools: e.target.value})} 
                    placeholder="e.g., ChatGPT, Copilot"
                    data-testid="other-ai-tools"
                  />
                </div>
              </div>
            </div>
            
            <LocationForm />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={saving} data-testid="create-company-btn">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Create Company
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Company Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Edit Company</DialogTitle></DialogHeader>
          <div className="space-y-6 py-4">
            <div className="space-y-2">
              <Label>Company Name *</Label>
              <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} />
            </div>
            
            {/* Tech Stack Section */}
            <div className="border-t pt-4">
              <h3 className="font-medium text-slate-900 mb-4">Tech Stack</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>ATS Used</Label>
                  <Input 
                    list="ats-options-edit"
                    value={formData.ats_used} 
                    onChange={(e) => setFormData({...formData, ats_used: e.target.value})} 
                    placeholder="e.g., JobDiva"
                  />
                  <datalist id="ats-options-edit">
                    {ATS_OPTIONS.map(opt => <option key={opt} value={opt} />)}
                  </datalist>
                </div>
                <div className="space-y-2">
                  <Label>AI Voice Using</Label>
                  <Input 
                    list="ai-voice-options-edit"
                    value={formData.ai_voice_using} 
                    onChange={(e) => setFormData({...formData, ai_voice_using: e.target.value})} 
                    placeholder="e.g., JobTalk"
                  />
                  <datalist id="ai-voice-options-edit">
                    {AI_VOICE_OPTIONS.map(opt => <option key={opt} value={opt} />)}
                  </datalist>
                </div>
                <div className="space-y-2">
                  <Label>Other AI Tools</Label>
                  <Input 
                    value={formData.other_ai_tools} 
                    onChange={(e) => setFormData({...formData, other_ai_tools: e.target.value})} 
                    placeholder="e.g., ChatGPT, Copilot"
                  />
                </div>
              </div>
            </div>
            
            <LocationForm />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleUpdate} disabled={saving} data-testid="update-company-btn">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Update Company
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Company</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{selectedCompany?.name}&quot;? This will also remove all associated locations. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Delete Confirmation */}
      <AlertDialog open={bulkDeleteDialogOpen} onOpenChange={setBulkDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selectedIds.size} Companies</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {selectedIds.size} selected companies? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleBulkDelete} className="bg-red-600 hover:bg-red-700">
              Delete {selectedIds.size} Companies
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
